import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EscalationAlert } from "./EscalationAlert";
import { EscalationsOverview } from "./EscalationsOverview";
import { RecognitionsOverview } from "./RecognitionsOverview";
import { SurveyHistoryOverview } from "./SurveyHistoryOverview";
import { SurveyCategoryWithBadges } from "../../../types";

interface OverviewTabProps {
  employeeId: number;
  surveyId?: string | number;
  onViewAllSurveyHistory: () => void;
  employeeData?: any;
  categoryAnalysisData?: any;
  categoryAnalysisLoading?: boolean;
  surveyHistoryData?: any[];
  surveyHistoryLoading?: boolean;
  user?: any;
  employee?: any;
  isFetchingUser?: boolean;
  isFetchingEmployee?: boolean;
  handleRecognitionPostSubmit?: (employeeId: string) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ 
  employeeId, 
  onViewAllSurveyHistory,
  employeeData,
  categoryAnalysisData,
  categoryAnalysisLoading,
  surveyHistoryData,
  surveyHistoryLoading,
  user,
  employee,
  isFetchingUser,
  isFetchingEmployee,
  handleRecognitionPostSubmit
}) => {
  const navigate = useNavigate();
  const [isEscalationAlertHidden, setIsEscalationAlertHidden] = useState(false);

  // Find the most recent escalation that needs attention
  const latestEscalation = React.useMemo(() => {
    if (!surveyHistoryData || surveyHistoryData.length === 0) return null;
    
    return [...surveyHistoryData]
      .filter(survey => 
        (survey.outcome === "NEEDS_ATTENTION" || survey.outcome === "AT_RISK") && 
        survey.escalation_id
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0] || null;
  }, [surveyHistoryData]);

  // Calculate escalation stats from survey history data
  const escalationStats = React.useMemo(() => {
    if (!surveyHistoryData || surveyHistoryData.length === 0) {
      return {
        total: 0,
        actionNeeded: 0,
        inProgress: 0,
        resolved: 0,
        dismissed: 0,
      };
    }

    const escalations = surveyHistoryData.filter(survey => survey.escalation_id);
    
    return {
      total: escalations.length,
      actionNeeded: escalations.filter(survey => 
        survey.outcome === "NEEDS_ATTENTION" || 
        survey.outcome === "AT_RISK"
      ).length,
      inProgress: escalations.filter(survey => survey.outcome === "IN_PROGRESS").length,
      resolved: escalations.filter(survey => survey.outcome === "RESOLVED").length,
      dismissed: escalations.filter(survey => survey.outcome === "DISMISSED").length,
    };
  }, [surveyHistoryData]);

  // Event handlers
  const handleViewEscalationTicket = () => {
    if (latestEscalation?.escalation_id && latestEscalation?.survey_id) {
      navigate(`/retention/v3/employees/${employeeId}/survey/${latestEscalation.survey_id}/escalation/${latestEscalation.escalation_id}`);
    }
  };

  const handleCloseEscalationAlert = () => {
    setIsEscalationAlertHidden(true);
  };

  const handleViewAllEscalations = () => {
    // TODO: Implement view all escalations
    console.log("View all escalations for employee:", employeeId);
  };

  const handleSendRecognitions = () => {
    // TODO: Implement send recognitions
    console.log("Send recognitions for employee:", employeeId);
  };



  // Mock data - in a real app, this would come from API calls
  const recognitionStats = {
    total: 0,
    birthday: 0,
    milestones: 0,
    performance: 0,
  };

  const upcomingCelebrations = [
    { type: "Work Anniversary", date: "Apr 15, 2024" },
    { type: "Birthday", date: "May 20, 2024" },
  ];

  const surveyCategories: SurveyCategoryWithBadges[] = [
    {
      type: "concern" as const,
      title: "Areas of Concern",
      badges: ["Career Growth", "Management", "Onboarding", "Onboarding", "Purpose", "Team Support", "Onboarding"],
    },
    {
      type: "neutral" as const,
      title: "Neutral Areas",
      badges: ["Team Communication", "Workload", "Belongings"],
    },
    {
      type: "strength" as const,
      title: "Strengths",
      badges: ["Employee Support"],
    },
  ];

  const surveyCategoriesInsufficient: SurveyCategoryWithBadges[] = [
    {
      type: "concern" as const,
      title: "Areas of Concern",
      insufficientData: true,
      badges: [],
    },
    {
      type: "neutral" as const,
      title: "Neutral Areas",
      insufficientData: true,
      badges: [],
    },
    {
      type: "strength" as const,
      title: "Strengths",
      insufficientData: true,
      badges: [],
    },
  ];

  return (
    <div className="flex w-full bg-neutral-50 max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-4 sm:gap-6 px-3 sm:px-6 lg:px-12 pt-4 sm:pt-6 pb-6 sm:pb-8">
      {surveyHistoryLoading ? (
        <div className="flex w-full items-center justify-center py-4">
          <span className="text-body font-body text-subtext-color">Loading escalation data...</span>
        </div>
      ) : latestEscalation && !isEscalationAlertHidden ? (
        <EscalationAlert
          title={`Escalation - ${latestEscalation.title}`}
          description={"Survey requires immediate attention."}
          buttonText="View Escalation Ticket"
          onViewTicket={handleViewEscalationTicket}
          onClose={handleCloseEscalationAlert}
        />
      ) : null}
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 sm:gap-6">
        <div className="flex w-full flex-col lg:flex-row items-stretch gap-4 sm:gap-6">
          <EscalationsOverview
            stats={escalationStats}
            onViewAll={handleViewAllEscalations}
          />
          <RecognitionsOverview
            stats={recognitionStats}
            upcomingCelebrations={upcomingCelebrations}
            onSendRecognitions={handleSendRecognitions}
            user={user}
            employee={employee}
            isFetchingUser={isFetchingUser}
            isFetchingEmployee={isFetchingEmployee}
            handleRecognitionPostSubmit={handleRecognitionPostSubmit}
          />
        </div>
        {/* <HealthStatusChart
          chartData={healthChartData}
          chartCategories={healthChartCategories}
          areasOfConcern={areasOfConcern}
          onViewDetails={handleViewHealthDetails}
        /> */}
        <SurveyHistoryOverview
          title="Survey History"
          employeeId={employeeId}
          categories={surveyCategories.length > 0 ? surveyCategories : surveyCategoriesInsufficient}
          onViewAll={onViewAllSurveyHistory}
          employeeData={employeeData}
          categoryAnalysisData={categoryAnalysisData}
          categoryAnalysisLoading={categoryAnalysisLoading}
        />
      </div>
    </div>
  );
}; 